import { useContext, useEffect, useRef, useState } from 'react'

import { Icon, Text, TextField } from '@joor/design-system'
import { Option } from '@joor/design-system'
import { useSelector } from 'react-redux'

import { isCreateCustomPoRestricted } from 'store/currentUser/selectors'

import { ImportOrdersModalIds } from 'modals/OrderList/OrderImportModal/orderImportModal.ids'

import { ImportOrdersContext } from '../../ImportOrders.context'
import { ImportOrdersStandardContext } from '../../ImportOrdersStandard.context'
import { useGetDropdownProps } from '../SecondStep.hooks'
import {
  AlertContainer,
  Container,
  StyledDropdown,
  TextFieldWrapper,
} from './SecondStepStandard.styles'

const SecondStepStandard = (): JSX.Element => {
  const {
    brandWarehousesOptions,
    connectedAccountsOptions,
    door,
    doorsOptions,
    includeDoor,
    isLoadingConnectedAccounts,
    isLoadingSecondStepSharedData,
    openedDropdown,
    orderType,
    orderTypeOptions,
    retailer,
    retailerSearchText,
    retailerWarehousesOptions,
    warehouse,
    setDoor,
    setOpenedDropdown,
    setOrderType,
    setRetailer,
    setRetailerSearchText,
    setWarehouse,
    setNextDisabled,
  } = useContext(ImportOrdersContext)

  const {
    poNumber,
    setPoNumber,
    disableRetailerDropdown,
    includedRetailerFound,
    setIncludedRetailerFound,
  } = useContext(ImportOrdersStandardContext)

  const [retailerSearchQuery, setRetailerSearchQuery] = useState('')
  const retailerSearchTimeout = useRef<NodeJS.Timeout | null>(null)
  const restrictCreateCustomPo = useSelector(isCreateCustomPoRestricted)

  const warehouseOptions =
    !retailer || !retailerWarehousesOptions.length
      ? brandWarehousesOptions
      : retailerWarehousesOptions

  const getDropdownProps = useGetDropdownProps(
    openedDropdown,
    setOpenedDropdown,
  )

  useEffect(() => {
    setNextDisabled(true)
    let mandatoryFieldsFilled: boolean = !!retailer

    if (doorsOptions.length > 0 && !includeDoor) {
      mandatoryFieldsFilled = mandatoryFieldsFilled && !!door
    }

    if (mandatoryFieldsFilled) setNextDisabled(false)
  }, [retailer, door, doorsOptions, includeDoor, setNextDisabled])

  const onRetailerSelected = ({ selected }: { selected: Option }) => {
    if (retailer?.id === selected.id) return
    setRetailer(selected)
    setDoor(null)
    setWarehouse(null)
    setRetailerSearchQuery('')
    setIncludedRetailerFound(undefined)
  }

  const onRetailerInputChange = (inputText: string) => {
    setRetailerSearchQuery(inputText)
    if (retailerSearchTimeout.current) {
      clearTimeout(retailerSearchTimeout.current)
    }
    retailerSearchTimeout.current = setTimeout(() => {
      if (inputText !== retailerSearchText) {
        setRetailerSearchText(inputText)
      }
    }, 500)
  }

  return (
    <Container>
      {includedRetailerFound !== undefined && (
        <AlertContainer className={includedRetailerFound ? 'info' : 'warning'}>
          <Icon
            iconName={includedRetailerFound ? 'information' : 'exclamation'}
            iconColor={includedRetailerFound ? 'secondary-800' : 'warning-800'}
            plain
          />
          <Text
            fontSize="small"
            fontWeight="semibold"
            letterSpacing="tighter"
            lineHeight="20"
            color={includedRetailerFound ? 'secondary-800' : 'warning-800'}
          >
            {includedRetailerFound
              ? "We've preselected the retailer you included in your template(s)."
              : "We couldn't find the retailer you included in your template(s)."}
          </Text>
        </AlertContainer>
      )}
      <StyledDropdown
        id={ImportOrdersModalIds.RetailerDropdown}
        testId={ImportOrdersModalIds.RetailerDropdown}
        label="Retailer"
        isRequired
        isLoading={isLoadingConnectedAccounts}
        isDisabled={disableRetailerDropdown}
        options={connectedAccountsOptions}
        value={retailer}
        translateOptions={false}
        isSearchable
        inputValue={retailerSearchQuery}
        onSelect={onRetailerSelected}
        placeholder="Start typing to search..."
        onInputChange={onRetailerInputChange}
        dropdownIndicatorIconName="search"
        isDropdownIndicatorIconAnimated={false}
        {...getDropdownProps('retailer')}
      />
      {!includeDoor && doorsOptions.length > 0 && (
        <StyledDropdown
          id={ImportOrdersModalIds.SearchDoorDropdownId}
          testId={ImportOrdersModalIds.SearchDoorDropdownId}
          label="Door"
          labelId={ImportOrdersModalIds.SearchDoorTitleId}
          isRequired
          isDisabled={isLoadingSecondStepSharedData}
          isLoading={isLoadingSecondStepSharedData}
          onSelect={({ selected }: { selected: Option }) => setDoor(selected)}
          options={doorsOptions}
          placeholder="Select one"
          value={door}
          {...getDropdownProps('door')}
        />
      )}
      {!!warehouseOptions.length && (
        <StyledDropdown
          id={ImportOrdersModalIds.WarehouseDropdown}
          testId={ImportOrdersModalIds.WarehouseDropdown}
          label="Warehouse"
          options={warehouseOptions}
          value={warehouse}
          isDisabled={isLoadingSecondStepSharedData}
          isLoading={isLoadingSecondStepSharedData}
          translateOptions={false}
          onSelect={({ selected }: { selected: Option }) =>
            setWarehouse(selected)
          }
          placeholder="Select one"
          {...getDropdownProps('warehouse')}
        />
      )}
      <StyledDropdown
        id={ImportOrdersModalIds.OrderTypeDropdown}
        testId={ImportOrdersModalIds.OrderTypeDropdown}
        label="Order Type"
        options={orderTypeOptions}
        value={orderType}
        isDisabled={isLoadingSecondStepSharedData}
        isLoading={isLoadingSecondStepSharedData}
        translateOptions={false}
        onSelect={({ selected }: { selected: Option }) =>
          setOrderType(selected)
        }
        placeholder="Select one"
        {...getDropdownProps('orderType')}
      />
      <TextFieldWrapper>
        <TextField
          id={ImportOrdersModalIds.CustomPONumberInput}
          data-testid={ImportOrdersModalIds.CustomPONumberInput}
          label="PO#"
          onChange={(value) => setPoNumber(value.target.value)}
          placeholder="Custom PO#"
          disabled={restrictCreateCustomPo}
          value={poNumber}
        />
      </TextFieldWrapper>
    </Container>
  )
}

export default SecondStepStandard
